import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import { Toast } from '../components/ui/Notification';
import { 
  FiUsers, FiInbox, FiClock, FiAlertTriangle, FiPlus, 
  FiArrowRight, FiRefreshCcw, FiActivity, FiLoader 
} from 'react-icons/fi';

export default function AdminDashboard() {
  const { adminUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notification, setNotification] = useState(null);
  const [error, setError] = useState(null);

  const normalizeDashboardData = useCallback((rawStats, clients = []) => {
    // 1. Map backend snake_case to frontend camelCase
    const normalized = {
      activeClients: rawStats?.active_clients || 0,
      trialClients: rawStats?.trial_clients || 0,
      expiredClients: rawStats?.expired_clients || 0,
      expiringSoon: rawStats?.expiring_soon || 0,
      leadsToday: rawStats?.leads_today || 0,
      leadsThisMonth: rawStats?.leads_this_month || 0,
      blockedTotal: rawStats?.blocked_total || 0,
      failedAlerts: rawStats?.errors_total || 0
    };

    // 2. Fallback: If stats are empty/zero but we have client data, calculate from list
    if (clients.length > 0 && normalized.activeClients === 0) {
      normalized.activeClients = clients.filter(c => c.status === 'Active').length;
      normalized.trialClients = clients.filter(c => c.status === 'Trial').length;
      normalized.expiredClients = clients.filter(c => c.status === 'Expired' || c.status === 'Grace').length;
      
      // Calculate expiring soon (0-7 days)
      normalized.expiringSoon = clients.filter(c => 
        c.days_remaining !== undefined && 
        c.days_remaining >= 0 && 
        c.days_remaining <= 7
      ).length;
    }

    return normalized;
  }, []);

  const fetchDashboardData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const token = adminUser.admin_session_token;
      
      // Fetch stats and clients in parallel for fallback/validation
      const [statsRes, clientsRes] = await Promise.all([
        adminApi.getStats(token).catch(() => ({ success: false })),
        adminApi.getClients(token).catch(() => ({ success: false }))
      ]);

      if (statsRes.success || clientsRes.success) {
        const normalized = normalizeDashboardData(
          statsRes.stats || {}, 
          clientsRes.clients || []
        );
        setStats(normalized);
        
        if (isManual) {
          setNotification({ message: 'Dashboard updated.', type: 'success' });
        }
      } else {
        setError("Dashboard stats could not be loaded. Client and lead records may still be available from the sidebar.");
      }
    } catch (e) {
      console.error('Dashboard fetch error:', e);
      setError("Connection failure. Check gateway URL in settings.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (adminUser) fetchDashboardData();
  }, [adminUser]);

  const cards = [
    { label: 'Active Clients', val: stats?.activeClients || 0, icon: FiUsers, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { label: 'Leads Today', val: stats?.leadsToday || 0, icon: FiInbox, color: 'text-brand-blue', bg: 'bg-brand-blue/5' },
    { label: 'Expiring Soon', val: stats?.expiringSoon || 0, icon: FiClock, color: 'text-amber-500', bg: 'bg-amber-50' },
    { label: 'Failed Alerts', val: stats?.failedAlerts || 0, icon: FiAlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  ];

  if (loading) return (
    <div className="animate-pulse space-y-8">
      <div className="h-10 w-48 bg-slate-200 rounded-lg"></div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-32 bg-white rounded-3xl"></div>)}
      </div>
      <div className="h-64 bg-white rounded-[2.5rem]"></div>
    </div>
  );

  return (
    <div className="space-y-10">
      {notification && <Toast {...notification} onClose={() => setNotification(null)} />}
      
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter">System Overview</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-slate-500 font-bold text-sm uppercase tracking-widest opacity-60">Operations Intelligence</p>
            <button 
              onClick={() => fetchDashboardData(true)} 
              disabled={refreshing}
              className="p-1 text-slate-400 hover:text-brand-blue transition-colors disabled:opacity-30"
            >
              <SafeIcon icon={refreshing ? FiLoader : FiRefreshCcw} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        <button 
          onClick={() => navigate('/admin/clients/new')} 
          className="px-8 py-4 bg-brand-blue text-white rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center gap-3 shadow-xl shadow-brand-blue/30 hover:-translate-y-1 transition-all active:scale-95"
        >
          <SafeIcon icon={FiPlus} /> Add New Client
        </button>
      </header>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-3xl p-6 flex items-center gap-4 text-red-600">
          <SafeIcon icon={FiAlertTriangle} className="text-xl flex-shrink-0" />
          <p className="text-xs font-bold">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map(card => (
          <div key={card.label} className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 relative overflow-hidden group">
            <div className={`absolute top-0 right-0 w-24 h-24 ${card.bg} rounded-full -mr-12 -mt-12 transition-transform group-hover:scale-110`}></div>
            <div className={`w-12 h-12 ${card.bg} ${card.color} rounded-2xl flex items-center justify-center mb-6 relative z-10 shadow-sm`}>
              <SafeIcon icon={card.icon} className="text-xl" />
            </div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{card.label}</p>
            <p className="text-4xl font-black text-slate-900 tracking-tighter">{card.val}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-[2.5rem] p-10 border border-slate-100 shadow-sm">
          <h3 className="text-xl font-black text-slate-900 mb-8 uppercase tracking-tight">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[ 
              { label: 'Manage Clients', to: '/admin/clients', desc: 'Edit access & dates' },
              { label: 'Lead Archive', to: '/admin/leads', desc: 'View system-wide leads' },
              { label: 'Audit Logs', to: '/admin/logs', desc: 'Review system events' },
              { label: 'Gateway Settings', to: '/admin/settings', desc: 'System health check' },
            ].map(action => (
              <button 
                key={action.label} 
                onClick={() => navigate(action.to)} 
                className="text-left p-6 bg-slate-50 hover:bg-brand-blue hover:text-white rounded-3xl transition-all group border border-slate-100"
              >
                <p className="font-black uppercase tracking-widest text-[11px] mb-1">{action.label}</p>
                <p className="text-[10px] opacity-60 font-medium">{action.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-navy-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden shadow-2xl">
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-brand-cyan/10 rounded-full blur-3xl -mb-32 -mr-32"></div>
          <h3 className="text-xl font-black mb-6 uppercase tracking-tight">Gateway Health</h3>
          <div className="space-y-6">
            <div className="flex items-center justify-between py-4 border-b border-white/5">
              <span className="text-xs font-bold text-slate-400">Connection Status</span>
              <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg text-[10px] font-black uppercase tracking-widest">Stable</span>
            </div>
            <div className="flex items-center justify-between py-4 border-b border-white/5">
              <span className="text-xs font-bold text-slate-400">Errors Tracked</span>
              <span className={`text-xs font-black ${(stats?.failedAlerts || 0) > 0 ? 'text-red-400' : 'text-white'}`}>
                {stats?.failedAlerts || 0}
              </span>
            </div>
            <button 
              onClick={() => navigate('/admin/settings')} 
              className="w-full mt-4 flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-brand-cyan hover:text-white transition-colors group"
            >
              Run Full Diagnostic <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}